/* build 2026-08-13T11:13:07 */
/* Serial Seal — template catalog with capability metadata.
 *
 * A template declares WHAT IT CAN DO; the builder renders both the gallery card
 * and the Step-2 form from that declaration. Nothing is inferred from a
 * template's name, and no control is hardcoded per design — add a template here
 * and the whole UI adapts.
 *
 * caps:
 *   category        'cards' | 'sports' | 'arcane' | 'creature'
 *                              (finished art, nothing to upload — one genre
 *                               per gallery chip, see SS_CATEGORIES)
 *                 | 'designer' (the pre-genre bucket; still honoured so a
 *                               studio-saved design keeps resolving)
 *                 | 'logo'     (needs a transparent PNG logo)
 *                 | 'artwork'  (needs customer artwork)
 *   requiresArtwork / requiresLogo   block ordering until supplied
 *   supportsTitle / supportsAccent   show the control only when true
 *   supportsQR / qrRequired          QR field appears only when supportsQR
 *   supportedLines                   template text lines a customer may edit
 *
 * Rendering fields (consumed by SS_SEAL, unchanged):
 *   window 'full' | 'window' | 'circle'   art placement
 *   plate  'dark' | 'holo'                flood ink vs bare foil
 *   supports {qr, logo, run_total}        LEGACY shape the compositor reads
 *   art / image / fit                     vector art, baked bitmap, crop nudge
 *
 * BACKWARD COMPATIBILITY: every pre-existing key is preserved exactly
 * (f_full, f_window, f_circle, f_tall, f_holo, f_logo_full, f_logo_window,
 * v_minimal, v_prism, v_marble). Orders and Label Farm payloads referencing
 * them keep resolving. `caps` is additive — `supports` is still the source of
 * truth for the renderer, and normalise() derives one from the other so
 * studio-saved designs written before caps existed still work.
 */
(function () {
  'use strict';

  var ART = window.SS_ART;

  /* ——— VIBRANT ART PALETTES (owner: "colorize them, vibrant, Pokémon-like") ———
     The baked art is authored in a 5-tone structural ink family; each design
     maps those five slots onto its own saturated palette in ss-seal.js artSvg().
     s1 ← #0c0d13 (dominant), s2 ← #14161f, s3 ← #1c1f2b/#141a26 (mid),
     s4 ← #a9b0c6 (the POP tone), s5 ← #000/#111116/#0a0c14 (deepest anchor).
     HOLO RULE: saturated and mid-dark only (L≈25-48%) — pastels vanish on
     foil; white stays foil; type/serial/watermark stay rich black elsewhere. */
  var PALETTES = {
    d_dragon:   { s1: '#4c1d95', s2: '#6d28d9', s3: '#312e81', s4: '#c026d3', s5: '#2e1065' },
    d_guardian: { s1: '#1e3a8a', s2: '#2563eb', s3: '#1e40af', s4: '#d97706', s5: '#172554' },
    d_crown:    { s1: '#92400e', s2: '#b45309', s3: '#7c2d12', s4: '#d97706', s5: '#451a03' },
    v_prism:    { s1: '#6d28d9', s2: '#0e7490', s3: '#be123c', s4: '#ea580c', s5: '#1e293b' },
    d_cyber:    { s1: '#0f766e', s2: '#0e7490', s3: '#134e4a', s4: '#0891b2', s5: '#042f2e' },
    d_vortex:   { s1: '#4338ca', s2: '#6d28d9', s3: '#1e1b4b', s4: '#db2777', s5: '#312e81' },
    d_phoenix:  { s1: '#b91c1c', s2: '#ea580c', s3: '#7f1d1d', s4: '#d97706', s5: '#450a0a' },
    v_marble:   { s1: '#065f46', s2: '#047857', s3: '#064e3b', s4: '#0d9488', s5: '#022c22' },
    d_sunset:   { s1: '#be185d', s2: '#7c3aed', s3: '#9d174d', s4: '#ea580c', s5: '#4a044e' },
    d_crest:    { s1: '#b91c1c', s2: '#a16207', s3: '#7f1d1d', s4: '#d97706', s5: '#422006' },

    /* SPORTS — team-colour thinking: one dominant club tone, one loud rival
       accent in s4 so the crest reads from arm's length. */
    s_pennant:  { s1: '#991b1b', s2: '#b91c1c', s3: '#7f1d1d', s4: '#d97706', s5: '#450a0a' },
    s_jersey:   { s1: '#1e3a8a', s2: '#1d4ed8', s3: '#1e40af', s4: '#b91c1c', s5: '#172554' },
    s_trophy:   { s1: '#92400e', s2: '#b45309', s3: '#78350f', s4: '#ca8a04', s5: '#451a03' },
    s_stitch:   { s1: '#9a3412', s2: '#c2410c', s3: '#7c2d12', s4: '#b91c1c', s5: '#431407' },
    s_stadium:  { s1: '#115e59', s2: '#0f766e', s3: '#134e4a', s4: '#0891b2', s5: '#042f2e' },
    s_ticket:   { s1: '#9f1239', s2: '#be123c', s3: '#881337', s4: '#b45309', s5: '#4c0519' },

    /* ARCANE — ink-and-candlelight: cold violets and teals carrying the form,
       s4 the one warm or opposing tone that keeps it off flat purple. */
    a_sigil:    { s1: '#5b21b6', s2: '#6d28d9', s3: '#4c1d95', s4: '#c026d3', s5: '#2e1065' },
    a_grimoire: { s1: '#7c2d12', s2: '#9a3412', s3: '#422006', s4: '#a16207', s5: '#1c1917' },
    a_portal:   { s1: '#0e7490', s2: '#0891b2', s3: '#155e75', s4: '#7c3aed', s5: '#083344' },
    a_wyrm:     { s1: '#4c1d95', s2: '#6d28d9', s3: '#3b0764', s4: '#15803d', s5: '#2e1065' },
    a_blade:    { s1: '#1e40af', s2: '#2563eb', s3: '#1e3a8a', s4: '#0891b2', s5: '#172554' },
    a_pentad:   { s1: '#9d174d', s2: '#be123c', s3: '#701a75', s4: '#0d9488', s5: '#4a044e' },

    /* CREATURE — elemental identity first: the whole seal should say fire,
       water, leaf, storm before the eye finds the drawing. */
    k_flame:    { s1: '#b91c1c', s2: '#ea580c', s3: '#9a3412', s4: '#d97706', s5: '#450a0a' },
    k_aqua:     { s1: '#0369a1', s2: '#0284c7', s3: '#075985', s4: '#0d9488', s5: '#082f49' },
    k_leaf:     { s1: '#15803d', s2: '#16a34a', s3: '#14532d', s4: '#a16207', s5: '#052e16' },
    k_bolt:     { s1: '#6d28d9', s2: '#7e22ce', s3: '#4c1d95', s4: '#ca8a04', s5: '#2e1065' },
    k_egg:      { s1: '#b45309', s2: '#d97706', s3: '#92400e', s4: '#be123c', s5: '#451a03' },
    k_orb:      { s1: '#0e7490', s2: '#0891b2', s3: '#155e75', s4: '#c026d3', s5: '#083344' }
  };

  /* A designer seal: finished artwork, nothing for the customer to upload.
     `cat` is the gallery genre and is LAST on purpose — every call written
     before genres existed omits it and keeps working, landing in 'cards'
     where the ten card seals belong. Retired designs inherit it too; their
     genre is inert because sellable() drops them before the chip filter. */
  function designer(key, name, blurb, motif, accent, brand, qr, retired, cat) {
    return {
      key: key, name: name, desc: blurb, family: 'designer',
      /* retired = keep the definition so past orders still render and reprint,
         but never offer it for sale again. See SS_SELLABLE below. */
      retired: !!retired,
      window: 'window', plate: 'holo',
      supports: { qr: qr !== false, logo: false, run_total: true },
      defaults: { brand: brand, color: accent },
      palette: PALETTES[key] || null,
      caps: {
        category: cat || 'cards',
        requiresArtwork: false, requiresLogo: false,
        supportsTitle: true, supportsAccent: true,
        supportsQR: qr !== false, qrRequired: false,
        allowsLogo: true,
        supportedLines: ['title']
      },
      art: function () { return ART.get(key, motif, key); }
    };
  }

  var TEMPLATES = [

    /* ————————————————— DESIGNER SEALS ————————————————— */

    /* ————— CARD-FOCUSED DESIGNER SEALS (2026-07-29) —————
       Built on the hobby's own objects rather than generic fantasy art. New
       KEYS on purpose: the old designs below are retired, not repurposed,
       because a shipped order carries its design key (ss1002 is d_crest) and
       reusing a key would show that customer — and a reprint — the wrong art. */
    designer('c_slab', 'Graded Slab',
      'The slab itself: label bar up top, the card flashing foil below it.',
      'slab', '#1e40af', 'HOLO HUNTERS', true),

    designer('c_gem', 'Gem Mint 10',
      'A struck grading medallion, laurels and a cut foil core.',
      'gemmint', '#a16207', 'TOP POP CARDS', true),

    designer('c_pack', 'Booster Pack',
      'A sealed foil pack, crimped edge and a shine band across the front.',
      'boosterpack', '#b91c1c', 'BASE SET CARDS', true),

    designer('c_rip', 'Pack Rip',
      'The moment of the pull — a torn edge with light bursting through.',
      'packrip', '#c2410c', 'EMBER BREAKS', true),

    designer('c_back', 'Card Back',
      'A classic patterned back: lattice weave around a centre medallion.',
      'cardback', '#5b21b6', 'TRAINER VAULT', true),

    designer('c_sleeve', 'Toploader',
      'A card in its rigid sleeve, glare cutting across the plastic.',
      'toploader', '#0f766e', 'GRADED PULLS', true),

    designer('c_fan', 'Card Fan',
      'Three cards fanned out, the front one cut clean to bare foil.',
      'cardfan', '#4c1d95', 'YOUR BRAND', true),

    designer('c_stack', 'Deck Stack',
      'A stack seen low and heavy, foil glinting between every card edge.',
      'deckstack', '#4a4a58', 'TOP TRAINER CARDS', true),

    designer('c_chase', 'Chase Hit',
      'A starburst that reads as the card everyone is breaking for.',
      'chasehit', '#be185d', 'NEXT LEVEL BREAKS', true),

    designer('c_grade', 'Centering',
      'The grader’s reticle — registration marks, crosshair and fine ticks.',
      'centering', '#0369a1', 'PRECISION PULLS', true),

    /* ————— SPORTS SEALS (2026-08-01) —————
       Original heraldry only. A pennant, a cup, a stitched ball — the
       furniture of the game, never a real club, league or jersey identity. */
    designer('s_pennant', 'Pennant',
      'A stadium pennant flying off its staff, streamers trailing behind it.',
      'pennant', '#b91c1c', 'RIVALRY BREAKS', true, false, 'sports'),

    designer('s_jersey', 'Game Jersey',
      'A hanging jersey with the number punched clean through the ink.',
      'jersey', '#1d4ed8', 'HOME TEAM CARDS', true, false, 'sports'),

    designer('s_trophy', 'Championship Cup',
      'A two-handled cup on its plinth, ribbon rays fanning out behind.',
      'trophycup', '#a16207', 'TITLE RUN CARDS', true, false, 'sports'),

    designer('s_stitch', 'Stitched Ball',
      'Two ball seams sweeping the length of the seal, every stitch cut to foil.',
      'stitching', '#c2410c', 'SANDLOT BREAKS', true, false, 'sports'),

    designer('s_stadium', 'Stadium Lights',
      'The bowl seen under floodlight — tiers in ink, the pitch left shining.',
      'stadium', '#0f766e', 'NIGHT GAME CARDS', true, false, 'sports'),

    designer('s_ticket', 'Game Ticket',
      'A perforated stub, torn edge and punched row, straight off the gate.',
      'ticket', '#be123c', 'FRONT ROW BREAKS', true, false, 'sports'),

    /* ————— ARCANE SEALS (2026-08-01) —————
       Invented occult furniture: our own glyphs, our own drake, our own
       five schools. Nothing borrowed from a published magic system.
       That applies to the DEFAULT BRAND STRINGS too — they print on the
       seal and sit in the gallery preview, so they must be invented shop
       names, never a real set, game or publisher wordmark. */
    designer('a_sigil', 'Binding Sigil',
      'A drawn circle of invented glyphs with the ring left as raw light.',
      'sigil', '#5b21b6', 'GLYPHWARD CARDS', true, false, 'arcane'),

    designer('a_grimoire', 'Grimoire',
      'A clasped tome, ribbon marker and gilt corners struck into the plate.',
      'grimoire', '#7c2d12', 'OLD TOME BREAKS', true, false, 'arcane'),

    designer('a_portal', 'Rift Portal',
      'A standing arch with the way through it left as open foil.',
      'portal', '#0e7490', 'RIFTGATE PULLS', true, false, 'arcane'),

    designer('a_wyrm', 'Coiled Wyrm',
      'A serpent-drake wound into a coil, every scale punched down to bare foil.',
      'wyrm', '#4c1d95', 'DRAKEHOLD CARDS', true, false, 'arcane'),

    designer('a_blade', 'Runed Blade',
      'A greatsword point-down, cut clean through an inked shield.',
      'blade', '#1e40af', 'IRONVOW COLLECTIBLES', true, false, 'arcane'),

    /* ⚠ The riskiest brief in this file: five marks around a ring is also the
       geometry of a published game's colour wheel. The five marks must be OUR
       glyphs — no sun, water drop, skull, fireball or tree, and no pentagram
       of linking lines between them. Draw the ring, not the wheel. */
    designer('a_pentad', 'Five Schools',
      'Five elemental marks set around a ring — an order of our own making.',
      'pentad', '#9d174d', 'FIVE SCHOOLS BREAKS', true, false, 'arcane'),

    /* ————— CREATURE SEALS (2026-08-01) —————
       Elemental crests, drawn originally. Read the element first, the
       drawing second — and nothing here is a two-tone banded sphere. */
    designer('k_flame', 'Flame Crest',
      'An ember crest with the fire punched clean out of the ink.',
      'flamecrest', '#c2410c', 'CINDER DEN CARDS', true, false, 'creature'),

    designer('k_aqua', 'Tide Crest',
      'A curling wave crest, the spray left as bare shine.',
      'aquacrest', '#0369a1', 'TIDEPOOL PULLS', true, false, 'creature'),

    designer('k_leaf', 'Verdant Crest',
      'A leaf crest, the blade left as foil with its veins struck over it.',
      'leafcrest', '#15803d', 'WILDGROVE CARDS', true, false, 'creature'),

    designer('k_bolt', 'Storm Crest',
      'A bolt struck across a chamfered crest — the strike itself is pure foil.',
      'boltcrest', '#7e22ce', 'STORMCALL BREAKS', true, false, 'creature'),

    designer('k_egg', 'Hatchling Egg',
      'A cracked shell with the light of whatever is inside coming through.',
      'eggshell', '#b45309', 'FIRST HATCH CLUB', true, false, 'creature'),

    designer('k_orb', 'Energy Orb',
      'A faceted stone blazing in a taloned mount.',
      'energyorb', '#0891b2', 'CORE ENERGY CARDS', true, false, 'creature'),

    /* ——— retired: kept so past orders render; not for sale ——— */
    designer('d_dragon', 'Arcane Dragon',
      'A dark fantasy dragon design with exposed holographic highlights.',
      'scales', '#6d28d9', 'YOUR BRAND', true, true),

    designer('d_guardian', 'Celestial Guardian',
      'A winged guardian in heavy silhouette, haloed by bare foil.',
      'wings', '#1e40af', 'GRADED PULLS', true, true),

    designer('d_crown', 'Gilded Crown',
      'An ornate crown and banner with ceremonial rays behind it.',
      'crown', '#a16207', 'BASE SET CARDS', true, true),

    designer('v_prism', 'Prism Burst',
      'Angular foil facets that shift around your brand and serial number.',
      'facets', '#5b21b6', 'TRAINER VAULT', true, true),

    designer('d_cyber', 'Cyber Grid',
      'A futuristic circuit design for breakers and modern card brands.',
      'grid', '#0f766e', 'NEXT LEVEL BREAKS', true, true),

    designer('d_vortex', 'Cosmic Vortex',
      'A collapsing spiral with a core of raw holographic light.',
      'spiral', '#4c1d95', 'TOP TRAINER CARDS', true, true),

    designer('d_phoenix', 'Phoenix Rise',
      'A rising phoenix cut out of dark cloud so the foil reads as fire.',
      'wings', '#c2410c', 'EMBER BREAKS', true, true),

    designer('v_marble', 'Marble Luxe',
      'Ink veining drifting across bare holographic stone. The quiet one.',
      'veins', '#4a4a58', 'TOP TRAINER CARDS', true, true),

    designer('d_sunset', 'Retro Sunset',
      'A sliced sun over a perspective grid — arcade nostalgia, cleanly drawn.',
      'sunset', '#be185d', 'RETRO PULLS', true, true),

    designer('d_crest', 'Champion Crest',
      'A heraldic shield, laurels and trophy struck into the plate.',
      'crest', '#a16207', 'CHAMPION CARDS', true, true),

    /* ————————————————— LOGO SEALS ————————————————— */
    {
      key: 'f_logo_full', name: 'Clean Foil', family: 'logo',
      desc: 'Bare holographic foil — your name, your serial, and a motif if you want one. Add a logo and it drops straight in.',
      window: 'full', plate: 'holo',
      supports: { qr: true, logo: true, run_total: true },
      defaults: { brand: 'YOUR BRAND', color: '#5b21b6' },
      /* NOT requiresLogo (owner 2026-08-12: "clean foil doesnt require a logo
         ... they can just put a name and moltif"). This is the tile most people
         land on first (#svq-clean), and demanding a file before the CTA would
         work turned the default path into a dead end for anyone who just wants
         their name on foil. A logo is still SUPPORTED here — it drops into the
         same full window when one is uploaded. */
      caps: { category: 'logo', requiresArtwork: false, requiresLogo: false,
              supportsTitle: true, supportsAccent: true,
              supportsQR: true, qrRequired: false, supportedLines: ['title'] }
    },
    {
      key: 'f_logo_window', retired: true, name: 'Logo Framed', family: 'logo',
      desc: 'Your logo up top, a bordered window beneath it.',
      window: 'window', plate: 'holo',
      supports: { qr: true, logo: true, run_total: true },
      defaults: { brand: 'MASTER SET BREAKS', color: '#374151' },
      caps: { category: 'logo', requiresArtwork: false, requiresLogo: true,
              supportsTitle: true, supportsAccent: true,
              supportsQR: true, qrRequired: false, supportedLines: ['title'] }
    },
    {
      key: 'v_minimal', retired: true, name: 'Minimal Logo', family: 'logo',
      desc: 'Plate, rule and number. Your logo carries the whole design.',
      window: 'window', plate: 'holo',
      supports: { qr: true, logo: true, run_total: true },
      defaults: { brand: '', color: '#475569' },
      caps: { category: 'logo', requiresArtwork: false, requiresLogo: true,
              supportsTitle: false, supportsAccent: true,
              supportsQR: true, qrRequired: false, supportedLines: [] },
      art: function () { return ART.get('v_minimal', 'ring', 'minimal'); }
    },
    {
      key: 'l_shield', retired: true, name: 'Shield Logo', family: 'logo',
      desc: 'Your logo struck inside a heraldic shield outline.',
      window: 'window', plate: 'holo',
      supports: { qr: true, logo: true, run_total: true },
      defaults: { brand: 'SHADOWLESS CO', color: '#a16207' },
      caps: { category: 'logo', requiresArtwork: false, requiresLogo: true,
              supportsTitle: true, supportsAccent: true,
              supportsQR: true, qrRequired: false, supportedLines: ['title'] },
      art: function () { return ART.get('l_shield', 'crest', 'shield'); }
    },
    {
      key: 'l_carbon', retired: true, name: 'Carbon Badge', family: 'logo',
      desc: 'Woven technical plate — matte and serious.',
      window: 'window', plate: 'holo',
      supports: { qr: true, logo: true, run_total: true },
      defaults: { brand: 'FIRST EDITION CO', color: '#374151' },
      caps: { category: 'logo', requiresArtwork: false, requiresLogo: true,
              supportsTitle: true, supportsAccent: true,
              supportsQR: true, qrRequired: false, supportedLines: ['title'] },
      art: function () { return ART.get('l_carbon', 'carbon', 'carbon'); }
    },
    {
      key: 'l_cyber', retired: true, name: 'Cyber Frame', family: 'logo',
      desc: 'Tech keylines, a plated serial and QR brackets — your logo behind glass.',
      window: 'window', plate: 'holo', chrome: 'tech',
      supports: { qr: true, logo: true, run_total: true },
      defaults: { brand: 'HOLO HUNTERS', color: '#0e7490' },
      caps: { category: 'logo', requiresArtwork: false, requiresLogo: true,
              supportsTitle: true, supportsAccent: true,
              supportsQR: true, qrRequired: false, supportedLines: ['title'] },
      /* circuit motif fills the window until a logo lands, so the gallery
         tile is never an empty frame */
      art: function () { return ART.get('l_cyber', 'grid', 'cyber'); }
    },

    /* ————————————————— UPLOAD ARTWORK ————————————————— */
    /* The customer-background seal (2026-08-02): their image bleeds the whole
       die and the bg-zone system (framed window, translucent panels, white
       type) composes over it on all four shapes — the same rendering path a
       Seal Studio bg design takes, fed per customer through fields.bg instead
       of design.bg. window:'full' is load-bearing twice: the zoned layouts
       ignore it once a bg exists, but SS_UPLOAD.target() reads it to quote
       the FULL DIE (L.W × L.H) as the upload target, and the empty-state
       preview renders as bare foil rather than a stranded window frame.
       An uploaded logo lands INSIDE the framed window, like any bg design. */
    {
      key: 'f_bg_upload', name: 'Upload Your Own', family: 'artwork',
      desc: 'Your own design fills the whole seal — name, serial and QR print over it.',
      window: 'full', plate: 'holo',
      supports: { qr: true, logo: false, run_total: true },
      defaults: { brand: 'YOUR BRAND', color: '#5b21b6' },
      caps: { category: 'artwork', requiresArtwork: false, requiresLogo: false,
              requiresBackground: true, allowsLogo: true,
              supportsTitle: true, supportsAccent: true,
              supportsQR: true, qrRequired: false, supportedLines: ['title'] }
    },
    {
      key: 'f_full', retired: true, name: 'Full Bleed', family: 'artwork',
      desc: 'Your artwork fills the seal behind the serial number and QR code.',
      window: 'full', plate: 'holo',
      supports: { qr: true, logo: false, run_total: true },
      defaults: { brand: 'YOUR BRAND', color: '#5b21b6' },
      caps: { category: 'artwork', requiresArtwork: true, requiresLogo: false,
              allowsLogo: true,
              supportsTitle: true, supportsAccent: true,
              supportsQR: true, qrRequired: false, supportedLines: ['title'] }
    },
    {
      key: 'f_window', retired: true, name: 'Framed Window', family: 'artwork',
      desc: 'Your artwork sits inside a bordered collectible-card frame.',
      window: 'window', plate: 'holo',
      supports: { qr: true, logo: false, run_total: true },
      defaults: { brand: 'TRAINER VAULT', color: '#0369a1' },
      caps: { category: 'artwork', requiresArtwork: true, requiresLogo: false,
              allowsLogo: true,
              supportsTitle: true, supportsAccent: true,
              supportsQR: true, qrRequired: false, supportedLines: ['title'] }
    },
    {
      key: 'f_circle', retired: true, name: 'Medallion', family: 'artwork',
      desc: 'Designed for circular logos, emblems, or character artwork.',
      window: 'circle', plate: 'holo',
      supports: { qr: true, logo: false, run_total: true },
      defaults: { brand: 'BASE SET CARDS', color: '#a16207' },
      caps: { category: 'artwork', requiresArtwork: true, requiresLogo: false,
              allowsLogo: true,
              supportsTitle: true, supportsAccent: true,
              supportsQR: true, qrRequired: false, supportedLines: ['title'] }
    },
    {
      key: 'f_tall', retired: true, name: 'Tall Panel', family: 'artwork',
      desc: 'Your artwork gets maximum room and the number goes big.',
      window: 'window', plate: 'holo',
      supports: { qr: true, logo: false, run_total: true },
      defaults: { brand: 'GRADED PULLS', color: '#be123c' },
      caps: { category: 'artwork', requiresArtwork: true, requiresLogo: false,
              allowsLogo: true,
              supportsTitle: true, supportsAccent: true,
              supportsQR: true, qrRequired: false, supportedLines: ['title'] }
    },
    {
      key: 'f_holo', retired: true, name: 'Holo Field', family: 'artwork',
      desc: 'Maximum exposed holographic foil with minimal printed artwork.',
      window: 'window', plate: 'holo',
      supports: { qr: true, logo: false, run_total: true },
      defaults: { brand: 'TOP TRAINER CARDS', color: '#5b3fa8' },
      caps: { category: 'artwork', requiresArtwork: true, requiresLogo: false,
              allowsLogo: true,
              supportsTitle: true, supportsAccent: true,
              supportsQR: true, qrRequired: false, supportedLines: ['title'] }
    }
  ];

  /* Fill in caps for anything that predates the schema — studio exports and
     localStorage designs written before caps existed. `supports` stays the
     source of truth for the renderer either way. */
  function normalise(t) {
    var s = t.supports || (t.supports = { qr: true, logo: false, run_total: true });
    if (!t.caps) {
      var cat = t.family === 'logo' ? 'logo' : (t.image || t.art ? 'designer' : 'artwork');
      t.caps = {
        category: cat,
        requiresArtwork: cat === 'artwork' && !t.image,
        requiresLogo: !!s.logo,
        supportsTitle: true,
        supportsAccent: true,
        supportsQR: !!s.qr,
        qrRequired: false,
        supportedLines: ['title']
      };
    }
    var c = t.caps;
    if (c.supportsQR == null) c.supportsQR = !!s.qr;
    if (c.supportedLines == null) c.supportedLines = c.supportsTitle === false ? [] : ['title'];
    /* the renderer only ever reads `supports`, so keep the two in step */
    s.qr = !!c.supportsQR;
    if (c.requiresLogo) s.logo = true;
    return t;
  }

  /* Studio-created designs (frame + baked bitmap) are merged in front, so the
     owner's finished work leads the gallery. */
  /* A studio entry may carry base:'<template key>' — it IS that template with
     a background under it. Resolve by copying the base and applying the
     studio's overrides, so the published design inherits caps, supports,
     window mode and even the art function without restating any of them. */
  function resolveBase(d) {
    if (!d || !d.base) return d;
    var b = null;
    for (var i = 0; i < TEMPLATES.length; i++) {
      if (TEMPLATES[i].key === d.base) { b = TEMPLATES[i]; break; }
    }
    if (!b) return d;                    // unknown base: render what we can
    var out = {};
    for (var k in b) out[k] = b[k];
    out.key = d.key; out.name = d.name || b.name; out.desc = d.desc || b.desc;
    out.bg = d.bg || '';
    /* per-shape studio crops (bg = portrait-single stays the fallback) */
    out.bg_pf = d.bg_pf || '';
    out.bg_ls = d.bg_ls || '';
    out.bg_lf = d.bg_lf || '';
    out.retired = !!d.retired;
    if (d.defaults) {
      out.defaults = { brand: d.defaults.brand || b.defaults.brand,
                       color: d.defaults.color || b.defaults.color };
    }
    var caps = {};
    for (var c in b.caps) caps[c] = b.caps[c];
    if (d.category) caps.category = d.category;
    /* the studio background IS this design's artwork — never ask the
       customer for another upload (the base frame's requiresArtwork is
       about CUSTOMER-artwork frames, not studio designs) */
    if (out.bg) { caps.requiresArtwork = false; caps.requiresBackground = false; }
    out.caps = caps;
    return out;
  }

  function loadSaved() {
    var out = [];
    try {
      var raw = localStorage.getItem('ss_designs');
      if (raw) out = out.concat(JSON.parse(raw) || []);
    } catch (_) {}
    if (window.SS_DESIGNS && window.SS_DESIGNS.length) {
      var have = {};
      out.forEach(function (d) { have[d.key] = 1; });
      window.SS_DESIGNS.forEach(function (d) { if (!have[d.key]) out.push(d); });
    }
    return out.map(resolveBase).map(normalise);
  }

  function build() {
    var saved = loadSaved(), have = {};
    saved.forEach(function (d) { have[d.key] = 1; });
    /* a studio design overrides a built-in with the same key */
    return saved.concat(TEMPLATES.filter(function (t) { return !have[t.key]; }).map(normalise));
  }

  window.SS_FRAMES = TEMPLATES;
  window.SS_TEMPLATES = build();
  window.SS_RELOAD = function () {
    window.SS_TEMPLATES = build();
    window.SS_SELLABLE = sellable();
    return window.SS_TEMPLATES;
  };

  /* ——— WHAT IS ON SALE ———
     Every genre plus the logo and upload layouts. The ten fantasy seals that
     the card set replaced (d_* and friends) carry retired:true instead, and
     that distinction is load-bearing:
     SS.tpl() resolves a design by key out of SS_TEMPLATES, and past orders
     carry those keys (ss1002 is d_crest). Dropping the definitions would make
     that customer's My Seals card render "Preview unavailable" and would break
     a reprint through tools/render_seals.py — SS.tpl() falls back to
     SS_TEMPLATES[0], so it would silently print the WRONG design.
     So: SS_TEMPLATES stays complete for lookups and history;
         SS_SELLABLE is what the builder gallery may show.
     To offer a category again, add its key to SELLABLE_CATS. */
  /* WIPED FROM SALE 2026-08-02 (owner: "wipe all previous designs, this is
     the new one going forward") — the four genre categories of vector designer
     seals are off the storefront. Definitions stay: shipped orders resolve
     their design keys forever. The catalog going forward is Seal Studio
     background designs (published into SS_DESIGNS with a base + bg) plus the
     logo and upload-artwork paths. To resurrect a genre, add its key back. */
  var SELLABLE_CATS = { logo: 1, artwork: 1 };
  function sellable() {
    var list = window.SS_TEMPLATES.filter(function (t) {
      if (t.retired) return false;
      if (t.bg) return true;   /* studio bg designs sell in every category */
      return SELLABLE_CATS[(t.caps && t.caps.category) || 'artwork'];
    });
    /* "Upload your own" LEADS the gallery (owner 2026-08-05) — it is the only
       option that takes the customer's own artwork, so it should not trail
       ten ready-made designs. */
    list.sort(function (a, b) {
      var A = (a.caps && a.caps.requiresBackground) ? 0 : 1;
      var B = (b.caps && b.caps.requiresBackground) ? 0 : 1;
      return A - B;
    });
    return list;
  }
  window.SS_SELLABLE = sellable();

  /* Customer-facing category labels — the single place they are defined. Only
     categories present in SS_SELLABLE are rendered as chips, so with one
     category on sale the chip row hides itself. */
  window.SS_CATEGORIES = [
    { key: 'all', label: 'All designs' },
    { key: 'cards', label: 'Cards' },
    { key: 'sports', label: 'Sports' },
    { key: 'arcane', label: 'Arcane' },
    { key: 'creature', label: 'Creatures' },
    { key: 'logo', label: 'Logo seals' },
    { key: 'artwork', label: 'Upload artwork' },
    /* Last, and usually absent: normalise() tags a studio-saved design
       'designer' when it predates the genre split, and a chip with a zero
       count never renders. It only shows up if such a design is loaded. */
    { key: 'designer', label: 'Designer seals' }
  ];
})();
